import { useState } from "react";
import cronstrue from "cronstrue";

import type {
  BulkScheduleBody,
  BulkScheduleResponse,
  BulkScheduleTarget,
  Frequency,
  ScheduleType,
} from "@qlik-scheduler/shared";

import { Modal } from "../components/Modal.js";
import { Button } from "../components/Button.js";
import { ErrorAlert } from "../components/ErrorAlert.js";
import { useBulkSchedule } from "../api/queries.js";

interface Props {
  targets: BulkScheduleTarget[];
  open: boolean;
  onClose: () => void;
  onCreated: (result: BulkScheduleResponse) => void;
}

const FREQUENCY_OPTIONS: Array<{ value: Frequency; label: string }> = [
  { value: "HOURLY", label: "Hourly" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "CUSTOM", label: "Custom (cron)" },
];

// Pick a sane initial datetime-local value 5 minutes ahead of now.
function initialStartTime(): string {
  const d = new Date(Date.now() + 5 * 60_000);
  d.setSeconds(0, 0);
  // datetime-local wants YYYY-MM-DDTHH:MM in LOCAL time.
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function BulkScheduleModal({ targets, open, onClose, onCreated }: Props) {
  const [scheduleType, setScheduleType] = useState<ScheduleType>("RECURRING");
  const [frequency, setFrequency] = useState<Frequency>("DAILY");
  const [customCron, setCustomCron] = useState("");
  const [scheduleName, setScheduleName] = useState("Bulk reload");
  const [appendAppName, setAppendAppName] = useState(true);
  const [startTime, setStartTime] = useState(initialStartTime());
  const [timezone, setTimezone] = useState(browserTz());
  const [partial, setPartial] = useState(false);
  const [retryEnabled, setRetryEnabled] = useState(true);
  const [maxRetryAttempts, setMaxRetryAttempts] = useState(3);
  const [retryDelayMinutes, setRetryDelayMinutes] = useState(5);
  const [exponentialBackoff, setExponentialBackoff] = useState(false);

  const bulk = useBulkSchedule();

  const cronPreview = (() => {
    if (frequency !== "CUSTOM") return null;
    if (!customCron.trim()) return null;
    try {
      return cronstrue.toString(customCron.trim());
    } catch {
      return "Invalid cron expression";
    }
  })();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const body: BulkScheduleBody = {
      template: {
        scheduleName,
        scheduleType,
        frequency,
        cronExpression: frequency === "CUSTOM" ? customCron.trim() : undefined,
        startTime: new Date(startTime).toISOString(),
        timezone,
        partialReload: partial,
        retryEnabled,
        maxRetryAttempts,
        retryDelayMinutes,
        exponentialBackoff,
        notifyOnFailure: false,
        notifyEmails: [],
      },
      targets,
      appendAppNameToScheduleName: appendAppName,
    };
    bulk.mutate(body, {
      onSuccess: (result) => {
        onCreated(result);
        onClose();
      },
    });
  }

  return (
    <Modal open={open} onClose={onClose} title="Bulk schedule" size="lg">
      <form onSubmit={submit} className="space-y-6">
        <section>
          <h3 className="text-sm font-semibold text-ink">
            Apply to {targets.length} app{targets.length === 1 ? "" : "s"}
          </h3>
          <p className="mt-1 text-[12px] text-ink-soft">
            One schedule will be created per app, sharing the template below.
          </p>
          <div className="mt-3 max-h-32 overflow-y-auto rounded-card border border-line bg-bg/40 p-3 text-[12px] text-ink-soft">
            {targets.map((t) => (
              <div key={`${t.connectionId}:${t.appId}`} className="truncate">
                · {t.appName}
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <div>
            <label htmlFor="bs-name" className="field-label mb-1.5 block">
              Schedule name
            </label>
            <input
              id="bs-name"
              type="text"
              className="input"
              value={scheduleName}
              onChange={(e) => setScheduleName(e.target.value)}
              required
            />
            <label className="mt-2 inline-flex items-center gap-2 text-[12px] text-ink-soft">
              <input
                type="checkbox"
                checked={appendAppName}
                onChange={(e) => setAppendAppName(e.target.checked)}
                className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
              />
              Append app name to each schedule (e.g. "{scheduleName} · Sales
              Dashboard")
            </label>
          </div>

          <div>
            <label htmlFor="bs-freq" className="field-label mb-1.5 block">
              Frequency
            </label>
            <select
              id="bs-freq"
              className="input"
              value={frequency}
              onChange={(e) => {
                const next = e.target.value as Frequency;
                setFrequency(next);
                setScheduleType("RECURRING");
              }}
            >
              {FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {frequency === "CUSTOM" ? (
            <div>
              <label htmlFor="bs-cron" className="field-label mb-1.5 block">
                Cron expression
              </label>
              <input
                id="bs-cron"
                type="text"
                className="input font-mono"
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                placeholder="*/15 * * * *"
                required
              />
              {cronPreview ? (
                <p className="mt-1.5 text-[12px] italic text-ink-soft">
                  {cronPreview}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="bs-start" className="field-label mb-1.5 block">
                Start
              </label>
              <input
                id="bs-start"
                type="datetime-local"
                className="input"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="bs-tz" className="field-label mb-1.5 block">
                Timezone (IANA)
              </label>
              <input
                id="bs-tz"
                type="text"
                className="input"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                required
              />
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={partial}
              onChange={(e) => setPartial(e.target.checked)}
              className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
            />
            Partial reload
          </label>

          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={retryEnabled}
              onChange={(e) => setRetryEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
            />
            Retry on failure
          </label>

          {retryEnabled ? (
            <div className="grid grid-cols-1 gap-3 pl-6 sm:grid-cols-3">
              <div>
                <label htmlFor="bs-att" className="field-label mb-1.5 block">
                  Max attempts
                </label>
                <input
                  id="bs-att"
                  type="number"
                  min={1}
                  max={20}
                  className="input"
                  value={maxRetryAttempts}
                  onChange={(e) =>
                    setMaxRetryAttempts(Math.max(1, Number(e.target.value)))
                  }
                />
              </div>
              <div>
                <label htmlFor="bs-del" className="field-label mb-1.5 block">
                  Delay (min)
                </label>
                <input
                  id="bs-del"
                  type="number"
                  min={1}
                  max={1440}
                  className="input"
                  value={retryDelayMinutes}
                  onChange={(e) =>
                    setRetryDelayMinutes(Math.max(1, Number(e.target.value)))
                  }
                />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={exponentialBackoff}
                    onChange={(e) => setExponentialBackoff(e.target.checked)}
                    className="h-4 w-4 rounded border-line text-blue focus:ring-blue"
                  />
                  Backoff
                </label>
              </div>
            </div>
          ) : null}
        </section>

        {bulk.isError ? (
          <ErrorAlert>
            {bulk.error instanceof Error ? bulk.error.message : "Failed"}
          </ErrorAlert>
        ) : null}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={bulk.isPending}>
            {bulk.isPending
              ? "Creating…"
              : `Create ${targets.length} schedule${targets.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
